import { Popover, Tag } from "antd";
import dayjs from "dayjs";
import _ from "lodash";
import React from "react";
import styled from "styled-components";

import { ReactComponent as ICON_CLOSE_APP } from "@assets/svgs/ICON_CLOSE_APP.svg";
import { ReactComponent as ICON_DISPLAY_SETTING } from "@assets/svgs/ICON_DISPLAY_SETTING.svg";
import { ReactComponent as ICON_DOT_MENU } from "@assets/svgs/ICON_DOT_MENU.svg";
import { ReactComponent as ICON_FORCE_SYNC } from "@assets/svgs/ICON_FORCE_SYNC.svg";
import { ReactComponent as ICON_MEDIA_INFO } from "@assets/svgs/ICON_MEDIA_INFO.svg";
import { ReactComponent as ICON_PREDICTION } from "@assets/svgs/ICON_PREDICTION.svg";
import { ReactComponent as ICON_RELOAD_APP } from "@assets/svgs/ICON_RELOAD_APP.svg";
import { ReactComponent as ICON_RESET_APP } from "@assets/svgs/ICON_RESET_APP.svg";
import { ReactComponent as ICON_RESTART } from "@assets/svgs/ICON_RESTART.svg";
import { ReactComponent as ICON_ROTATE_APP } from "@assets/svgs/ICON_ROTATE_APP.svg";
import { ReactComponent as ICON_SHUTDOWN } from "@assets/svgs/ICON_SHUTDOWN.svg";
import { ReactComponent as ICON_TABLE_DELETE } from "@assets/svgs/ICON_TABLE_DELETE.svg";
import { ReactComponent as ICON_TABLE_EDIT } from "@assets/svgs/ICON_TABLE_EDIT.svg";
import { Text } from "@components/Text";
import { ContentInfo } from "@pages/DeviceListPage/ContentInfo";
import { COLOR_DARK_GREY, COLOR_GREEN, COLOR_RED } from "@static/COLORS";
import {
  KEY_DEVICE_MANAGEMENT_DEVICES_DELETE,
  KEY_DEVICE_MANAGEMENT_DEVICES_UPDATE,
} from "@static/PERMISSION_KEY";
import { convertFormatDate } from "@utils/convertFormatDate";

const SectionTableAction = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionActionDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* width: 200px;
  height: 300px; */
`;

const GroupActionDetails = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: start;
`;

const RotateContainer = styled.div`
  width: 125px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  /* align-items: center; */
`;

const BASE_DEVICE_LIST_TABLE_COLUMNS = [
  {
    title: "#",
    dataIndex: "index",
    key: "index",
    fixed: "left",
    width: 80,
    sorter: (a, b) => a.index - b.index,
  },
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    fixed: "left",
    width: 280,
  },
  {
    title: "Online Status",
    key: "is_online",
    align: "center",
    width: 180,
    render: (text, item) => {
      const isOnline = _.get(item, ["isOnline"]);
      const lastSeen = _.get(item, ["lastSeen"]) || "-";
      const formattedLastSeen =
        lastSeen === "-"
          ? "-"
          : dayjs(Number(lastSeen))
              .tz("Asia/Bangkok")
              .format("YYYY-MM-DD HH:mm:ss");

      return (
        <>
          <Text
            align="center"
            color={isOnline ? COLOR_GREEN : COLOR_RED}
            fontWeight="600"
          >
            {isOnline ? "Online" : "Offline"}
          </Text>
          <Text align="center" color={COLOR_DARK_GREY} fontWeight="500">
            {formattedLastSeen}
          </Text>
        </>
      );
    },
  },
  {
    title: "Download Status",
    align: "center",
    width: 180,
    render: (text, item) => {
      const downloadProgressList = _.get(item, ["download_progress"]);
      const isForceSync = _.chain(downloadProgressList)
        .get(["status"])
        .isEqual("downloading")
        .value();
      // console.log("downloadProgressList :>> ", downloadProgressList);
      const percentage = _.chain(downloadProgressList)
        .get(["percentage"])
        .thru((percentageItem) => {
          // console.log("percentageItem :>> ", percentageItem);
          return _.get(downloadProgressList, ["isDownloadFile"])
            ? `${percentageItem}%`
            : "-";
        })
        .value();

      const formattedTime = convertFormatDate({
        date: _.get(item, ["updated_at"]),
      });
      const isOnline = _.get(item, ["isOnline"]);

      return (
        <>
          <div
            style={{
              display: "flex",
              gap: "8px",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Text
              style={{ width: "90px" }}
              align="center"
              fontWeight="600"
              color={
                !isOnline ? COLOR_RED : isForceSync ? COLOR_GREEN : COLOR_GREEN
              }
            >
              {!isOnline ? "0%" : isForceSync ? "downloading" : `${percentage}`}
            </Text>
          </div>
          <Text align="center" color={COLOR_DARK_GREY} fontWeight="500">
            {formattedTime}
          </Text>
        </>
      );
    },
  },
  {
    title: "Type",
    dataIndex: "type",
    key: "type",
    width: 100,
    align: "center",
    render: (text, item) => {
      const type = _.get(item, ["type"]);
      let color;
      switch (type) {
        case "WINDOWS":
          color = "success";
          break;
        case "ANDROID":
          color = "error";
          break;
        default:
          color = "default";
      }
      return <Tag color={color}>{type}</Tag>;
    },
  },
  {
    title: "Details",
    key: "details",
    align: "center",
    width: 120,

    render: (text, item, index) => {
      return (
        <Popover
          content={<ContentInfo key={index} data={item} />}
          title="Details"
          trigger="click"
        >
          <ICON_MEDIA_INFO
            style={{ cursor: "pointer" }}
            fill={COLOR_DARK_GREY}
            width={"20px"}
            height={"20px"}
          />
        </Popover>
      );
    },
  },
];

export const getDeviceListTableColumns = ({
  permission,
  onHandleEdit,
  onHandleDelete,
  showConfirmation,
  onHandleGotoNestedCampaign,
  onHandleShutdownDevice,
  onHandleRestartDevice,
  onHandleCloseElectronApp,
  onHandleSoftSycGroup,
  onHandleForceSycGroup,
  onHandleRotateScreen,
  onHandleResetApp,
  onHandleReloadApp,
  handleRequestStartWhen,
}) => {
  const deviceListColumns = [...BASE_DEVICE_LIST_TABLE_COLUMNS];

  return [
    ...deviceListColumns,
    {
      title: "Actions",
      key: "actions",
      width: 120,

      render: (text, item, index) => {
        const isWindowApp = _.get(item, ["type"]) === "WINDOWS";
        const isOnline = _.get(item, ["isOnline"]);

        const isDisabled = !_.get(item, ["player_id"]);

        return (
          <SectionTableAction key={index}>
            <Popover
              placement="top"
              trigger="hover"
              content={<div>Prediction</div>}
            >
              <ICON_PREDICTION
                className={
                  isDisabled || !isOnline ? "icon-disabled" : "icon-clickable"
                }
                // className={"icon-disabled"}
                width={20}
                height={20}
                onClick={() => handleRequestStartWhen({ item })}
              />
            </Popover>
            <Popover
              placement="top"
              trigger="hover"
              content={<div>Go to campaign</div>}
            >
              <ICON_DISPLAY_SETTING
                // className={
                //   disabledNestedCampaign ? "icon-disabled" : "icon-clickable"
                // }
                // className={"icon-disabled"}
                className={"icon-clickable"}
                width={20}
                height={20}
                onClick={() => onHandleGotoNestedCampaign({ item })}
              />
            </Popover>
            {_.includes(permission, KEY_DEVICE_MANAGEMENT_DEVICES_UPDATE) && (
              <Popover
                placement="top"
                trigger="hover"
                content={<div>Edit</div>}
              >
                <ICON_TABLE_EDIT
                  width={24}
                  height={24}
                  onClick={() => onHandleEdit({ item })}
                />
              </Popover>
            )}

            <Popover
              placement="top"
              trigger="hover"
              content={<div>Soft Sync</div>}
            >
              <ICON_RESTART
                className={
                  !isOnline || isDisabled ? "icon-disabled" : "icon-clickable"
                }
                width={24}
                height={24}
                // onClick={() => onHandleSoftSycGroup({ item })}
                onClick={() =>
                  showConfirmation({
                    title: "Soft Sync",
                    onConfirm: () => onHandleSoftSycGroup({ item }),
                  })
                }
              />
            </Popover>

            <Popover
              placement="top"
              trigger="hover"
              content={<div>Force Sync</div>}
            >
              <ICON_FORCE_SYNC
                className={
                  !isOnline || isDisabled ? "icon-disabled" : "icon-clickable"
                }
                width={24}
                height={24}
                // onClick={() => onHandleForceSycGroup({ item })}
                onClick={() =>
                  showConfirmation({
                    title: "Force Sync",
                    onConfirm: () => onHandleForceSycGroup({ item }),
                  })
                }
              />
            </Popover>

            <Popover
              placement="bottomLeft"
              trigger="click"
              content={
                <SectionActionDetails>
                  {_.includes(
                    permission,
                    KEY_DEVICE_MANAGEMENT_DEVICES_DELETE
                  ) && (
                    <GroupActionDetails
                      onClick={() =>
                        showConfirmation({
                          title: "Delete",
                          onConfirm: () => onHandleDelete({ item }),
                        })
                      }
                    >
                      <ICON_TABLE_DELETE width={24} height={24} />
                      <Text>Delete</Text>
                    </GroupActionDetails>
                  )}

                  <Popover
                    placement="rightTop"
                    content={
                      <RotateContainer>
                        <Text
                          onClick={() =>
                            showConfirmation({
                              title: "Rotate",
                              onConfirm: () =>
                                onHandleRotateScreen({
                                  playerId: _.get(item, ["player_id"]),
                                  degree: 0,
                                }),
                            })
                          }
                        >
                          Landscape
                        </Text>
                        <Text
                          onClick={() =>
                            // onHandleRotateScreen({
                            //   playerId: _.get(item, ["player_id"]),
                            //   degree: 90,
                            // })
                            showConfirmation({
                              title: "Rotate",
                              onConfirm: () =>
                                onHandleRotateScreen({
                                  playerId: _.get(item, ["player_id"]),
                                  degree: 90,
                                }),
                            })
                          }
                        >
                          Portrait
                        </Text>
                        <Text
                          onClick={() =>
                            // onHandleRotateScreen({
                            //   playerId: _.get(item, ["player_id"]),
                            //   degree: 180,
                            // })
                            showConfirmation({
                              title: "Rotate",
                              onConfirm: () =>
                                onHandleRotateScreen({
                                  playerId: _.get(item, ["player_id"]),
                                  degree: 180,
                                }),
                            })
                          }
                        >
                          Landscape (flipped)
                        </Text>
                        <Text
                          onClick={() =>
                            // onHandleRotateScreen({
                            //   playerId: _.get(item, ["player_id"]),
                            //   degree: 270,
                            // })
                            showConfirmation({
                              title: "Rotate",
                              onConfirm: () =>
                                onHandleRotateScreen({
                                  playerId: _.get(item, ["player_id"]),
                                  degree: 270,
                                }),
                            })
                          }
                        >
                          Portrait (flipped)
                        </Text>
                      </RotateContainer>
                    }
                  >
                    <GroupActionDetails
                      className={
                        !isOnline || isDisabled
                          ? "icon-disabled"
                          : "icon-clickable"
                      }
                    >
                      <ICON_ROTATE_APP width={24} height={24} />
                      <Text>Rotate</Text>
                    </GroupActionDetails>
                  </Popover>

                  {isWindowApp && (
                    <GroupActionDetails
                      className={
                        !isOnline || isDisabled
                          ? "icon-disabled"
                          : "icon-clickable"
                      }
                      onClick={() =>
                        showConfirmation({
                          title: "Close App",
                          onConfirm: () => onHandleCloseElectronApp({ item }),
                        })
                      }
                    >
                      <ICON_CLOSE_APP width={24} height={24} />
                      <Text>Close App</Text>
                    </GroupActionDetails>
                  )}

                  {isWindowApp && (
                    <GroupActionDetails
                      className={
                        !isOnline || isDisabled
                          ? "icon-disabled"
                          : "icon-clickable"
                      }
                      onClick={() =>
                        showConfirmation({
                          title: "Restart",
                          onConfirm: () => onHandleRestartDevice({ item }),
                        })
                      }
                    >
                      <ICON_RESTART width={24} height={24} />
                      <Text>Restart</Text>
                    </GroupActionDetails>
                  )}

                  {isWindowApp && (
                    <GroupActionDetails
                      className={
                        !isOnline || isDisabled
                          ? "icon-disabled"
                          : "icon-clickable"
                      }
                      onClick={() =>
                        showConfirmation({
                          title: "Shutdown",
                          onConfirm: () => onHandleShutdownDevice({ item }),
                        })
                      }
                    >
                      <ICON_SHUTDOWN width={24} height={24} />
                      <Text>Shutdown Pc</Text>
                    </GroupActionDetails>
                  )}

                  <GroupActionDetails
                    className={isDisabled ? "icon-disabled" : "icon-clickable"}
                    onClick={() =>
                      showConfirmation({
                        title: "Reset App",
                        onConfirm: () => onHandleResetApp({ item }),
                      })
                    }
                  >
                    <ICON_RESET_APP width={24} height={24} />
                    <Text>Reset App</Text>
                  </GroupActionDetails>

                  <GroupActionDetails
                    className={isDisabled ? "icon-disabled" : "icon-clickable"}
                    onClick={() =>
                      showConfirmation({
                        title: "Reload App",
                        onConfirm: () => onHandleReloadApp({ item }),
                      })
                    }
                  >
                    <ICON_RELOAD_APP width={24} height={24} />
                    <Text>Reload App</Text>
                  </GroupActionDetails>
                </SectionActionDetails>
              }
            >
              <ICON_DOT_MENU width={20} height={20} />
            </Popover>
          </SectionTableAction>
        );
      },
    },
  ];
};
